import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface AssignClientData {
  clientId: string
  adminId: string
  previousAdminId?: string
}

export function useAssignClient() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ clientId, adminId, previousAdminId }: AssignClientData) => {
      // Update or create the new assignment as owner
      const { error } = await supabase
        .from('client_admin_assignments')
        .upsert(
          {
            client_id: clientId,
            admin_id: adminId,
            is_owner: true,
            can_edit: true,
            can_view: true,
            assigned_at: new Date().toISOString(),
          },
          {
            onConflict: 'client_id,admin_id',
          }
        )

      if (error) throw error

      // Remove owner status from previous admin
      if (previousAdminId && previousAdminId !== adminId) {
        await supabase
          .from('client_admin_assignments')
          .update({ is_owner: false })
          .eq('client_id', clientId)
          .eq('admin_id', previousAdminId)
      }

      // Log the action
      await supabase.from('notification_logs').insert({
        type: 'system',
        titre: 'Attribution client modifiee',
        message: `Client reattribue a un nouvel admin`,
        reference_type: 'user',
        reference_id: clientId,
        metadata: { previous_admin: previousAdminId, new_admin: adminId },
      })

      return { clientId, adminId }
    },
    onSuccess: ({ clientId }) => {
      queryClient.invalidateQueries({ queryKey: ['client', clientId] })
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      queryClient.invalidateQueries({ queryKey: ['admins'] })
    },
  })
}
